// clinicalTestBattery.js - Protocol Definitions, Empty Payloads & Styling Tokens

import {
  CLINICAL_TRANSFER_DURATION_SEC,
} from "../../../constants";

export const CLINICAL_TESTS = [
  {
    id: "test1_grossReach",
    number: 1,
    shortName: "Gross",
    title: "Gross Arm Reach",
    subtitle: "Action Research Arm Test (ARAT Gross Movement)",
    scale: "ARAT Gross (Max 9 pts)",
    emoji: "🦾",
    color: "#0e88eb",
    accentGrad: "linear-gradient(135deg, #0e88eb, #38a9f8)",
    glowColor: "rgba(14, 136, 235, 0.4)",
    maxScore: 9,
    duration: 30,
    demoImage: "/assets/clinical/frames/t1_step1_mouth.jpg",
    demoMotion: "Touch Mouth ➔ Top of Head ➔ Behind Head",
    demoTip: "Keep trunk upright against chair. Reach each target steadily without leaning your body sideways.",
    frames: [
      {
        step: 1,
        title: "Hand to Mouth",
        desc: "Raise your active hand cleanly to mouth level without leaning forward.",
        src: "/assets/clinical/frames/t1_step1_mouth.jpg",
      },
      {
        step: 2,
        title: "Top of Head",
        desc: "Lift hand higher to touch the crown / top of your head with elbow raised.",
        src: "/assets/clinical/frames/t1_step2_head.jpg",
      },
      {
        step: 3,
        title: "Behind Head",
        desc: "Reach your hand back behind your head / nape of neck smoothly.",
        src: "/assets/clinical/frames/t1_step3_behind.jpg",
      },
    ],
    zones: [
      { id: "mouth", label: "Hand to Mouth Level", emoji: "👄" },
      { id: "top_head", label: "Hand to Top of Head", emoji: "👑" },
      { id: "behind_head", label: "Hand Behind Head", emoji: "💆" },
    ],
    instructions: [
      "Sit upright facing the camera with head and shoulders clearly visible.",
      "Reach your active hand to touch each highlighted target zone in order.",
      "Hold your hand steady inside the zone for 0.6s until it rings green.",
      "Complete all 3 target zones: Mouth ➔ Top of Head ➔ Behind Head."
    ],
  },
  {
    id: "test2_objectTransfer",
    number: 2,
    shortName: "Virtual",
    title: "Virtual Object Transfer",
    subtitle: "Box & Block Test (BBT) & Virtual Transfer",
    scale: "BBT Continuous (1 pt/block, -0.1/wall hit)",
    emoji: "📦",
    color: "#0e88eb",
    accentGrad: "linear-gradient(135deg, #0e88eb, #38a9f8)",
    glowColor: "rgba(14, 136, 235, 0.4)",
    maxScore: null,
    duration: CLINICAL_TRANSFER_DURATION_SEC,
    demoImage: "/assets/clinical/frames/t2_demo_clinical.jpg",
    demoMotion: "Grab Left Block ➔ Carry Over Partition ➔ Drop in Right Box",
    demoTip: "Reach into the left container to grab a block, carry it smoothly across the central divider, and release into the right box.",
    frames: [
      {
        step: 1,
        title: "Box & Block Transfer Test",
        desc: "Reach into left box, pick up block, lift over partition wall, and release into right box. Repeat as many times as possible!",
        src: "/assets/clinical/frames/t2_demo_clinical.jpg",
      },
    ],
    instructions: [
      "Blocks appear on the LEFT source box.",
      "Hover active hand/wrist over the block (0.3s) to GRAB it.",
      "Carry across the central partition line into the RIGHT container.",
      "Hover (0.3s) inside target container to DROP. Transfer as many as possible in 60s!"
    ],
  },
  {
    id: "test3_pinch",
    number: 3,
    shortName: "Finger",
    title: "Finger Spread & Pinch",
    subtitle: "ARAT Pinch Subscale (21-Landmark Hands)",
    scale: "ARAT Pinch Continuous (Uncapped Speed Bonus)",
    emoji: "🤌",
    color: "#0e88eb",
    accentGrad: "linear-gradient(135deg, #0e88eb, #38a9f8)",
    glowColor: "rgba(14, 136, 235, 0.4)",
    maxScore: null,
    duration: 50,
    demoImage: "/assets/clinical/frames/t3_step1_open.jpg",
    demoMotion: "1. Finger Spread ➔ 2. Index ➔ 3. Middle ➔ 4. Ring ➔ 5. Pinky Pinch",
    demoTip: "Extend all 5 fingers wide apart in a star shape. Then cleanly pinch your thumb tip to each fingertip: Index, Middle, Ring, and Pinky in order.",
    frames: [
      {
        step: 1,
        title: "1. Finger Spread & Abduction",
        desc: "Hold active hand facing camera and extend all 5 fingers wide apart in a star shape.",
        src: "/assets/clinical/frames/t3_step1_open.jpg",
      },
      {
        step: 2,
        title: "2. Thumb + Index Tip Pinch",
        desc: "Touch thumb tip cleanly to index fingertip forming an O-circle.",
        src: "/assets/clinical/frames/t3_step2_index.jpg",
      },
      {
        step: 3,
        title: "3. Thumb + Middle Tip Pinch",
        desc: "Touch thumb tip cleanly to middle fingertip forming a circular pinch.",
        src: "/assets/clinical/frames/t3_step3_middle.jpg",
      },
      {
        step: 4,
        title: "4. Thumb + Ring Tip Pinch",
        desc: "Touch thumb tip cleanly to ring fingertip forming a precision pinch.",
        src: "/assets/clinical/frames/t3_step4_ring.jpg",
      },
      {
        step: 5,
        title: "5. Thumb + Pinky Tip Pinch",
        desc: "Touch thumb tip cleanly to little / pinky fingertip completing the sequence.",
        src: "/assets/clinical/frames/t3_step5_pinky.jpg",
      },
    ],
    poses: [
      { id: "open",   label: "Open Hand (Spread 5 Fingers Wide)",  emoji: "🖐️" },
      { id: "index",  label: "Thumb + Index Pinch",                 emoji: "👌" },
      { id: "middle", label: "Thumb + Middle Pinch",                emoji: "🤌" },
      { id: "ring",   label: "Thumb + Ring Pinch",                  emoji: "🤏" },
      { id: "pinky",  label: "Thumb + Little / Pinky Pinch",        emoji: "🤙" },
    ],
    instructions: [
      "Hold active hand clearly visible in front of webcam.",
      "Follow each prompted pose and hold for 0.6s to register.",
      "1. Spread all 5 fingers wide (Open hand extension)",
      "2. Touch Thumb cleanly to Index ➔ Middle ➔ Ring ➔ Pinky fingertips."
    ],
  },
  {
    id: "test4_reactionStrike",
    number: 4,
    shortName: "Timed",
    title: "Timed Reaction Strike",
    subtitle: "Wolf Motor Function Test (WMFT Timed Tasks)",
    scale: "WMFT Continuous (RT & Smoothness)",
    emoji: "⚡",
    color: "#0e88eb",
    accentGrad: "linear-gradient(135deg, #0e88eb, #38a9f8)",
    glowColor: "rgba(14, 136, 235, 0.4)",
    maxScore: null,
    duration: 40,
    demoImage: "/assets/clinical/frames/t4_demo_clinical.jpg",
    demoMotion: "Resting Ready Position ➔ Fast Direct Reach to Glowing Target",
    demoTip: "Keep hand ready. When the target flashes on screen, reach forward swiftly to touch it with maximum speed and precision.",
    frames: [
      {
        step: 1,
        title: "Reaction Time Strike",
        desc: "Keep active hand ready; when the reaction target appears, strike swiftly in a direct path to touch it.",
        src: "/assets/clinical/frames/t4_demo_clinical.jpg",
      },
    ],
    instructions: [
      "Keep active hand at a ready starting position.",
      "A glowing reaction target will appear at random coordinates.",
      "Move/strike your active hand into the target as fast as possible.",
      "10 trials total — measures reaction latency and movement smoothness (jerk)."
    ],
  },
  {
    id: "test5_romSweep",
    number: 5,
    shortName: "ROM",
    title: "ROM Sweep",
    subtitle: "Fugl-Meyer Assessment (FMA Proximal ROM)",
    scale: "FMA Proximal (Max 9 pts)",
    emoji: "📐",
    color: "#0e88eb",
    accentGrad: "linear-gradient(135deg, #0e88eb, #38a9f8)",
    glowColor: "rgba(14, 136, 235, 0.4)",
    maxScore: 9,
    duration: 90,
    demoImage: "/assets/clinical/frames/t5_step1_straight.jpg",
    demoMotion: "Pre-Check ➔ CW: Straight ➔ Slight Bend ➔ Folded ➔ CCW: Straight ➔ Slight Bend ➔ Folded",
    demoTip: "Step back 2–3 meters so your arm reach is framed without getting cut off. Complete the pre-check, then perform 3 rotation trajectories clockwise followed by 3 trajectories anti-clockwise: full reach, slight elbow bend, and folded elbow.",
    frames: [
      {
        step: 1,
        title: "Pre-Check: Step Back 2–3m",
        desc: "Step back until your entire arm span, shoulders, and wrists fit inside the screen frame.",
        src: "/assets/clinical/frames/t5_step0_stepback.jpg",
      },
      {
        step: 2,
        title: "Trajectory 1: Full Reach (Wide)",
        desc: "Reach arm naturally outward and rotate in a wide coronal circular sweep.",
        src: "/assets/clinical/frames/t5_step1_straight.jpg",
      },
      {
        step: 3,
        title: "Trajectory 2: Slight Elbow Bend (Mid)",
        desc: "Bend your elbow slightly (~120°) and rotate your arm in a medium-radius circular sweep.",
        src: "/assets/clinical/frames/t5_step2_slightbend.jpg",
      },
      {
        step: 4,
        title: "Trajectory 3: Folded Elbow (Compact)",
        desc: "Fold your elbow closer to your chest (~65°) and rotate your hand in a compact inner circle.",
        src: "/assets/clinical/frames/t5_step3_folded.jpg",
      },
    ],
    instructions: [
      "Pre-Check: Step 2–3 meters back, center shoulder, and reach wrist into boundary target box.",
      "Clockwise (CW ↻): 3 Trajectories — Full Reach, Slight Bend (~120°), Folded (~65°).",
      "Anti-Clockwise (CCW ↺): 3 Trajectories — Full Reach, Slight Bend (~120°), Folded (~65°).",
      "Direction cues will show you which way to sweep and auto-hide as soon as you begin.",
    ],
  },
];

export const emptyPayload = () => ({
  test1_grossReach: {
    zones: [
      { zone: "mouth", success: false, latency_ms: 0, shoulderAngle: 0, elbowAngle: 0, compensationDetected: false, score: 0 },
      { zone: "top_head", success: false, latency_ms: 0, shoulderAngle: 0, elbowAngle: 0, compensationDetected: false, score: 0 },
      { zone: "behind_head", success: false, latency_ms: 0, shoulderAngle: 0, elbowAngle: 0, compensationDetected: false, score: 0 },
    ],
    totalScore: 0,
    score: 0,
  },
  test2_objectTransfer: {
    durationSec: CLINICAL_TRANSFER_DURATION_SEC,
    blocksTransferred: 0,
    missCount: 0,
    collisionPenalty: 0,
    rawScore: 0,
    avgPickTime_ms: 0,
    avgDropTime_ms: 0,
    elbowAngle_mean: 0,
    shoulderAngle_mean: 0,
    aratProxyScore: 0,
    trials: [],
    score: 0,
  },
  test3_pinch: {
    handOpenSuccess: false,
    handOpenSpread: 0,
    pinchAttempts: [
      { finger: "open",   success: false, accuracy: 1, dwellActual_ms: 0, score: 0 },
      { finger: "index",  success: false, accuracy: 1, dwellActual_ms: 0, score: 0 },
      { finger: "middle", success: false, accuracy: 1, dwellActual_ms: 0, score: 0 },
      { finger: "ring",   success: false, accuracy: 1, dwellActual_ms: 0, score: 0 },
      { finger: "pinky",  success: false, accuracy: 1, dwellActual_ms: 0, score: 0 },
    ],
    totalScore: 0,
    score: 0,
  },
  test4_reactionStrike: {
    trials: [],
    trialScores: [],
    avgReactionTime_ms: 0,
    medianRT_ms: 0,
    p90RT_ms: 0,
    hitAccuracy: 0,
    movementJerk: 0,
    smoothnessScore: 0,
    reactionScore: 0,
    score: 0,
  },
  test5_romSweep: {
    maxShoulderAbduction_deg: 0,
    maxElbowExtension_deg: 0,
    maxVerticalAngle_deg: 0,
    circleCoveragePct: 0,
    cumulativeDegreesSwept: 0,
    lapsCompleted: 0,
    pathSimilarityPct: 0,
    avgAngularSpeed_deg_s: 0,
    trajectoryJerk: 0,
    sweepDuration_ms: 0,
    totalScore: 0,
    score: 0,
  },
});

export const S = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #070913 0%, #0d1224 50%, #080a14 100%)",
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    color: "#f1f5f9",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0.75rem 1rem",
    boxSizing: "border-box",
  },
  headerNav: {
    width: "100%",
    maxWidth: "1400px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.6rem",
    padding: "0 0.5rem",
  },
  progressPills: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  pill: (active, done, color) => ({
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 12px",
    borderRadius: "99px",
    fontSize: "0.75rem",
    fontWeight: 700,
    background: active ? color + "26" : done ? "rgba(16,185,129,0.15)" : "rgba(255,255,255,0.06)",
    color: active ? color : done ? "#10b981" : "#64748b",
    border: `1.5px solid ${active ? color : done ? "#10b98166" : "rgba(255,255,255,0.08)"}`,
    transition: "all 0.25s ease",
  }),
  armToggle: {
    display: "flex",
    background: "rgba(255,255,255,0.06)",
    borderRadius: "10px",
    padding: "3px",
    border: "1px solid rgba(255,255,255,0.1)",
  },
  armBtn: (active) => ({
    padding: "4px 10px",
    borderRadius: "7px",
    fontSize: "0.75rem",
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
    background: active ? "#6366f1" : "transparent",
    color: active ? "#fff" : "#94a3b8",
    transition: "all 0.2s ease",
  }),
  stageContainer: (isFullscreen) => ({
    position: "relative",
    width: isFullscreen ? "100vw" : "calc(100vw - 28px)",
    maxWidth: isFullscreen ? "100vw" : "1280px",
    height: isFullscreen ? "100vh" : "calc(100vh - 66px)",
    maxHeight: isFullscreen ? "100vh" : "800px",
    background: "#04060d",
    borderRadius: isFullscreen ? "0px" : "22px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: isFullscreen ? "none" : "1px solid rgba(255, 255, 255, 0.12)",
    margin: isFullscreen ? "0px" : "0 auto 10px auto",
    boxShadow: isFullscreen ? "none" : "0 20px 40px rgba(0,0,0,0.4)",
  }),
  videoElement: {
    position: "absolute",
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: "scaleX(-1)",
    opacity: 0.95,
  },
  canvasOverlay: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "auto",
  },
  floatingModule: {
    position: "absolute",
    background: "rgba(8, 12, 24, 0.85)",
    backdropFilter: "blur(18px)",
    border: "1px solid rgba(255, 255, 255, 0.14)",
    borderRadius: "14px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
    zIndex: 25,
  },
  modulePill: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "8px 14px",
  },
  timerCapsule: (isLowTime, color) => ({
    position: "absolute",
    top: "16px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "6px 18px",
    borderRadius: "99px",
    background: "rgba(8, 12, 24, 0.9)",
    border: `2px solid ${isLowTime ? "#ef4444" : color}`,
    boxShadow: `0 0 25px ${isLowTime ? "rgba(239,68,68,0.5)" : color + "44"}`,
    backdropFilter: "blur(20px)",
    zIndex: 30,
    transition: "all 0.25s ease",
  }),
  drawerTriggerTab: (color) => ({
    position: "absolute",
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(10, 14, 28, 0.92)",
    borderTop: `1.5px solid ${color}`,
    borderBottom: `1.5px solid ${color}`,
    borderLeft: `1.5px solid ${color}`,
    borderRight: "none",
    borderTopLeftRadius: "12px",
    borderBottomLeftRadius: "12px",
    padding: "12px 14px",
    color: "#fff",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    boxShadow: `-6px 0 25px ${color}33`,
    backdropFilter: "blur(16px)",
    zIndex: 30,
    transition: "all 0.2s ease",
  }),
  bottomControlBar: {
    position: "absolute",
    bottom: "14px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 8px",
    borderRadius: "999px",
    background: "rgba(6, 10, 20, 0.88)",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    backdropFilter: "blur(24px)",
    boxShadow: "0 12px 35px rgba(0,0,0,0.6)",
    zIndex: 30,
  },
  controlBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 14px",
    borderRadius: "999px",
    fontSize: "0.76rem",
    fontWeight: 600,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.06)",
    color: "#e2e8f0",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  drawerBackdrop: {
    position: "absolute",
    inset: 0,
    background: "rgba(0, 0, 0, 0.65)",
    backdropFilter: "blur(4px)",
    zIndex: 40,
  },
  drawerPanel: (isOpen) => ({
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 0,
    width: "420px",
    maxWidth: "92vw",
    background: "rgba(10, 14, 28, 0.96)",
    backdropFilter: "blur(25px)",
    borderLeft: "1.5px solid rgba(255, 255, 255, 0.16)",
    boxShadow: "-15px 0 50px rgba(0, 0, 0, 0.75)",
    zIndex: 45,
    transform: isOpen ? "translateX(0)" : "translateX(100%)",
    transition: "transform 0.32s cubic-bezier(0.16, 1, 0.3, 1)",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    padding: "1.5rem",
    boxSizing: "border-box",
  }),
  briefingCard: {
    width: "100%",
    maxWidth: "1140px",
    background: "rgba(15, 20, 38, 0.85)",
    border: "1.5px solid rgba(255, 255, 255, 0.14)",
    borderRadius: "24px",
    backdropFilter: "blur(25px)",
    boxShadow: "0 25px 70px rgba(0, 0, 0, 0.6)",
    padding: "2rem 2.25rem",
    boxSizing: "border-box",
  },
  primaryBtn: (grad) => ({
    padding: "12px 28px",
    borderRadius: "14px",
    border: "none",
    background: grad,
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.95rem",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
    transition: "all 0.2s ease",
  }),
  secondaryBtn: {
    padding: "10px 20px",
    borderRadius: "12px",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    background: "rgba(255, 255, 255, 0.05)",
    color: "#e2e8f0",
    fontWeight: 600,
    fontSize: "0.85rem",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    transition: "all 0.2s ease",
  },
};
